import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Poster,
} from "@vidstack/react";
import React, { useMemo, useRef } from "react";

import BigPlayButton from "../BigPlayButton";
import { Icons } from "../icons/icons";
import { VideoLayout } from "../VideoLayout";
import { cn } from "@/lib/utils";
import { frameSkipAmountMs } from "../VideoLayout/Buttons";
import lodash from "lodash";
import { useDataContext } from "@/app/_context";

export default function BusinessCardPlayer({
  portal,
}: {
  portal?: boolean;
  skeleton?: boolean;
}) {
  const {
    setCurrentPlaybackTime,
    session,
    setIsPaused,
    currentSessionIndex,
    setVideoCanPlay,
    videoCanplay,
    currentProjectInfo,
    setIsBusinessCardPlaying,
    setWasBusinessCardShown,
  } = useDataContext();

  const { throttle } = lodash;

  const businessCardPlayerRef = useRef<MediaPlayerInstance>(null);

  // Throttle the time update to only update every 1 second
  const throttledSetCurrentTime = useMemo(
    () =>
      throttle((time: number) => {
        setCurrentPlaybackTime(time);
      }, frameSkipAmountMs), // Throttle to update once per second
    [],
  );

  const handleTimeUpdate = (e: { currentTime: number }) => {
    const currentTime = e.currentTime; // Round to nearest second
    throttledSetCurrentTime(currentTime); // Throttle updates to state every second
  };

  return (
    <div className="aspect-video">
      <MediaPlayer
        className={cn(
          !portal
            ? "aspect-video"
            : "h-full flex items-center bg-red-500 absolute inset-0",
          "w-full group bg-slate-900 text-white font-sans overflow-hidden rounded-md ring-media-focus data-[focus]:ring-4",
        )}
        title={session?.title}
        src={currentProjectInfo.merchandiseVideoUrl}
        logLevel="silent"
        crossOrigin
        playsInline
        // autoPlay={autoplayState}
        autoPlay
        onCanPlay={() => businessCardPlayerRef.current && setVideoCanPlay(true)}
        onPosterChange={() =>
          businessCardPlayerRef.current && setVideoCanPlay(false)
        }
        onPause={() => setIsPaused(true)}
        onPlay={() => setIsPaused(false)}
        onEnded={() => {
          console.log("ended");
          setCurrentPlaybackTime(0);
          setIsBusinessCardPlaying(false);
          setWasBusinessCardShown(true);
        }}
        onTimeUpdate={handleTimeUpdate} // Attach throttled time update handler
        ref={businessCardPlayerRef}
      >
        <MediaProvider
          className={cn("[&>video]:-left-[31%] [&>video]:relative")}
        >
          {videoCanplay ? null : portal ? (
            currentSessionIndex === 0 ? null : (
              <div className="absolute inset-0 z-[1000] flex place-items-center justify-center pointer-events-none">
                <Icons.spinner className="w-12 h-12 animate-spin text-foreground" />
              </div>
            )
          ) : null}
          <Poster
            className={cn(
              portal
                ? "aspect-video blur-sm w-full absolute opacity-0 transition-opacity data-[visible]:opacity-100"
                : "absolute inset-0 block h-full w-full rounded-md opacity-0 transition-opacity data-[visible]:opacity-100 object-cover",
            )}
            src="https://upload.wikimedia.org/wikipedia/commons/c/ca/Peace_pipe.jpg"
            alt={"business card"}
          />
        </MediaProvider>
        <VideoLayout />
      </MediaPlayer>
    </div>
  );
}
